import { Box, Stack, Typography } from '@mui/material';
import type { BetWithMarketOutcomeEvent } from '../../../../@types';
import { checkIsWaysEventForReofferedBetslip } from '../../../../helpers';
import { useSkinnedReofferedColor } from '../../../../hooks/useSkinnedReofferedColor';
import { useMarketplaceConfig } from '../../../../queries/marketplace';
import { BetDetails } from '../../../atoms/BetDetails';
import OddsDisplay from '../../../atoms/OddsDisplay';

const styles = {
  root: (isWaysEvent: boolean) => ({
    position: 'relative',
    display: 'grid',
    alignItems: 'end',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'neutral.600',
    overflow: 'hidden',
    ...(isWaysEvent
      ? {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: 4,
            backgroundColor: 'success.main',
            pointerEvents: 'none',
            zIndex: 1,
          },
        }
      : {}),
  }),
  betCardBody: {
    minWidth: 0,
    px: 1.25,
    py: 1,
    display: 'grid',
    alignContent: 'center',
    gap: 0.625,
  },
  betMeta: {
    minWidth: 0,
    display: 'grid',
    gap: 0.25,
  },
  betOutcome: {
    typography: 'body2',
    fontWeight: 800,
    color: 'text.primary',
    textAlign: 'left',
  },
  betEvent: {
    typography: 'subtitle3',
    color: 'neutral.200',
  },
  betMarket: {
    typography: 'body3',
    color: 'neutral.200',
  },
  oddsColumn: {
    px: 1.25,
    py: 1,
    display: 'grid',
    justifyItems: 'end',
    alignContent: 'center',
  },
  oddsValue: {
    typography: 'body2',
    fontWeight: 800,
    textAlign: 'right',
  },
  previousValues: {
    textDecoration: 'line-through',
    opacity: 0.5,
  },
  bankerBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 24,
    height: 24,
    borderRadius: 1,
    backgroundColor: 'primary.main',
    color: 'background.default',
    typography: 'body3',
    fontWeight: 800,
  },
} as const;

type NeobetReofferedSelectionCardProps = {
  bet: BetWithMarketOutcomeEvent;
  previousBet?: BetWithMarketOutcomeEvent;
  bets: BetWithMarketOutcomeEvent[];
  showBankers: boolean;
};

const NeobetReofferedSelectionCard = ({ bet, previousBet, bets, showBankers }: NeobetReofferedSelectionCardProps) => {
  const { isFTBFeed } = useMarketplaceConfig();
  const getReofferedColor = useSkinnedReofferedColor();
  const isWaysEvent = checkIsWaysEventForReofferedBetslip(bet, bets);

  const { color: oddsColor, changed: oddsChanged } = getReofferedColor(bet.odds, previousBet?.odds);
  const { color: specialValuesColor, changed: specialValuesChanged } = getReofferedColor(
    isFTBFeed ? bet.outcome.properties?.specialValue : bet.specialValues,
    isFTBFeed ? previousBet?.outcome.properties?.specialValue : previousBet?.specialValues
  );

  const showPreviousOutcome = isFTBFeed && specialValuesChanged && previousBet;
  const showPreviousSpecialValues = specialValuesChanged && previousBet;
  const showPreviousOdds = oddsChanged && previousBet;
  const isBanker = bet.banker || false;

  return (
    <Box sx={styles.root(isWaysEvent)} data-testid={`neobet-reoffered-card-${bet.outcomeId}`}>
      <Box sx={styles.betCardBody}>
        <Box sx={styles.betMeta}>
          <Stack
            spacing={0.5}
            direction='row'
            sx={{
              alignItems: 'center',
            }}
          >
            {showPreviousOutcome && (
              <BetDetails.Outcome
                outcomeName={previousBet.outcome.name}
                outcomeShortName={previousBet.outcome.shortName}
                {...previousBet.outcome.properties}
                marketName={previousBet.market.name}
                marketTypeName={previousBet.market.marketType.name}
                marketDisplayType={previousBet.market.displayType}
                eventName={previousBet.event.name}
                sx={{ ...styles.betOutcome, ...styles.previousValues }}
              />
            )}
            <BetDetails.Outcome
              outcomeName={bet.outcome.name}
              outcomeShortName={bet.outcome.shortName}
              {...bet.outcome.properties}
              color={isFTBFeed ? specialValuesColor : undefined}
              marketName={bet.market.name}
              marketTypeName={bet.market.marketType.name}
              marketDisplayType={bet.market.displayType}
              eventName={bet.event.name}
              sx={styles.betOutcome}
            />
            {showPreviousSpecialValues && (
              <BetDetails.SpecialValues
                outcomeName={previousBet.outcome.name}
                specialValues={previousBet.specialValues}
                marketTypeName={previousBet.market.marketType.name}
                specialValueModel={previousBet.specialValues?.[0]?.model}
                sportId={previousBet.market.marketType.sportId}
                variant='body2'
                color='neutral.200'
                sx={styles.previousValues}
              />
            )}
            <BetDetails.SpecialValues
              outcomeName={bet.outcome.name}
              specialValues={bet.specialValues}
              marketTypeName={bet.market.marketType.name}
              specialValueModel={bet.specialValues?.[0]?.model}
              sportId={bet.market.marketType.sportId}
              color={specialValuesColor}
              variant='body2'
            />
          </Stack>
          <Typography sx={styles.betEvent}>{bet.event.name}</Typography>
          <BetDetails.Market market={bet.market} sx={styles.betMarket} />
        </Box>

        {showBankers && isBanker && <Box sx={styles.bankerBadge}>B</Box>}
      </Box>

      <Box sx={styles.oddsColumn}>
        {showPreviousOdds && (
          <OddsDisplay
            value={previousBet.odds}
            sx={{ ...styles.oddsValue, ...styles.previousValues, color: 'neutral.200' }}
          />
        )}
        <OddsDisplay value={bet.odds} sx={{ ...styles.oddsValue, color: oddsColor }} />
      </Box>
    </Box>
  );
};

export default NeobetReofferedSelectionCard;
